import { useEffect, useRef, useState } from "react";
import { X, Share2, Download, Loader2 } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { FanionBadge, FANION_LABELS } from "@/components/FanionBadge";
import { Button } from "@/components/ui/button";

/**
 * Victory share card modal — used when a stage is settled and the user gets on the
 * podium, or when their fanion levels up. Renders a shareable image with the
 * current fanion + a short message; users can download or share via the native
 * share sheet.
 *
 * Props:
 *   open (bool), onClose (fn)
 *   user (object): expects fanion_level, fanion_label, name
 *   title (string), subtitle (string), footer (string, optional)
 *   variant: "podium" | "fanion"
 */
export function VictoryShareCard({ open, onClose, user, title, subtitle, footer, variant = "podium" }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const level = user?.fanion_level || 1;
  const fanionLabel = user?.fanion_label || FANION_LABELS[level];

  const generate = async () => {
    if (!cardRef.current) return null;
    return toPng(cardRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#062A40",
    });
  };

  const download = async () => {
    setBusy(true);
    try {
      const dataUrl = await generate();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = variant === "fanion" ? "fanion-mpy.png" : "victoire-mpy.png";
      a.click();
      toast.success("Image téléchargée");
    } catch (e) {
      toast.error("Impossible de générer l'image");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const dataUrl = await generate();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "ma-petite-yole.png", { type: "image/png" });
      const text = `${title} — Ma Petite Yole. Prono. Vibré. Gagné.`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Ma Petite Yole", text });
      } else if (navigator.share) {
        await navigator.share({ title: "Ma Petite Yole", text });
      } else {
        const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(wa, "_blank");
      }
    } catch (e) {
      /* ignore */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="victory-modal"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-md p-6 space-y-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-slate-100"
          data-testid="victory-close"
          aria-label="Fermer"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="text-center">
          <div className="brand-signature text-xs text-[color:var(--coral)] mb-1">Prono. Vibré. Gagné.</div>
          <h2 className="brand-wordmark text-3xl text-[color:var(--deep)]">{title}</h2>
          {subtitle && <p className="text-sm text-slate-600 mt-2">{subtitle}</p>}
        </div>

        {/* Shareable card (rendered visible + captured to image) */}
        <div
          ref={cardRef}
          style={{
            width: "100%",
            aspectRatio: "9 / 12",
            background: "linear-gradient(155deg, #062A40 0%, #062A40 55%, #0A3852 100%)",
            color: "white",
            borderRadius: 24,
            padding: "28px 24px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontFamily: "'Barlow Condensed', 'Inter', sans-serif",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div style={{ position: "absolute", inset: 0, backgroundImage: "url(/branding/logo_full.png)", backgroundSize: "cover", backgroundPosition: "center", opacity: 0.18 }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 11, letterSpacing: 5, color: "#FFD447", fontWeight: 700, textTransform: "uppercase" }}>
              Ma Petite Yole
            </div>
            <div style={{ fontSize: 32, fontWeight: 900, textTransform: "uppercase", letterSpacing: -0.5, lineHeight: 1, marginTop: 8 }}>
              {variant === "fanion" ? "Nouveau fanion !" : "Ça a payé !"}
            </div>
          </div>

          <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 16 }}>
            <img
              src={`/branding/fanions/fanion-${level}-${["mousse","boisdresse","patron","metyole"][level-1]}.svg`}
              alt=""
              style={{ width: 96, height: "auto", filter: "drop-shadow(0 6px 20px rgba(0,0,0,0.4))" }}
            />
            <div>
              <div style={{ fontSize: 11, letterSpacing: 4, color: "#FFD447", fontWeight: 700, textTransform: "uppercase" }}>
                Niveau {level}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, textTransform: "uppercase", letterSpacing: -0.3, lineHeight: 1 }}>
                {fanionLabel}
              </div>
              <div style={{ fontSize: 12, opacity: 0.75, marginTop: 6, fontFamily: "'Inter', sans-serif" }}>
                {user?.name || "Un(e) parieur(se)"}
              </div>
            </div>
          </div>

          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 14, opacity: 0.92, fontFamily: "'Inter', sans-serif", lineHeight: 1.4 }}>
              {footer || subtitle || "Tour des Yoles Rondes · Martinique 2026"}
            </div>
            <div style={{ marginTop: 12, display: "inline-block", background: "#FFD447", color: "#062A40", padding: "6px 14px", borderRadius: 999, fontSize: 11, fontWeight: 800, letterSpacing: 3, textTransform: "uppercase" }}>
              Prono. Vibré. Gagné.
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={download}
            variant="outline"
            disabled={busy}
            data-testid="victory-download"
            className="flex-1 rounded-full h-11 text-xs font-bold"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
            Télécharger
          </Button>
          <Button
            onClick={share}
            disabled={busy}
            data-testid="victory-share"
            className="flex-1 rounded-full h-11 text-xs btn-cta"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
            Partager
          </Button>
        </div>
      </div>
    </div>
  );
}
